import { useLayoutEffect, useRef, useState } from "react";

const MARQUEE_SPEED_PX_PER_SEC = 120;
const MARQUEE_MIN_DURATION_SEC = 10;
const MARQUEE_MIN_GAP_PX = 48;

const Marquee = ({ data }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const itemRefs = useRef([]);
  const [gap, setGap] = useState(MARQUEE_MIN_GAP_PX);
  const [duration, setDuration] = useState(null);

  const hasData = Array.isArray(data) && data.length > 0;
  itemRefs.current = [];

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content || !hasData) return;

    // Measure the text items on their own (gap not yet applied) so the
    // remaining space can be split evenly across every item's trailing gap.
    const totalTextWidth = itemRefs.current.reduce((sum, el) => sum + (el?.offsetWidth || 0), 0);
    const containerWidth = container.offsetWidth;
    const gapPx = Math.max(MARQUEE_MIN_GAP_PX, (containerWidth - totalTextWidth) / data.length);
    const contentWidth = totalTextWidth + gapPx * data.length;
    const distance = containerWidth + contentWidth;
    const computedDuration = Math.max(distance / MARQUEE_SPEED_PX_PER_SEC, MARQUEE_MIN_DURATION_SEC);

    content.style.setProperty("--marquee-start", `${containerWidth}px`);
    content.style.setProperty("--marquee-end", `${-contentWidth}px`);
    setGap(gapPx);
    setDuration(computedDuration);
  }, [data, hasData]);

  if (!hasData) return null;

  return (
    <>
      <style>{`
        @keyframes marquee-slide {
          from { transform: translateX(var(--marquee-start, 100%)); }
          to { transform: translateX(var(--marquee-end, -100%)); }
        }
      `}</style>
      <div
        ref={containerRef}
        className="p-1 relative overflow-hidden whitespace-nowrap"
        style={{ backgroundColor: "var(--banner-bg-color)" }}
      >
        <div
          ref={contentRef}
          className="inline-flex"
          style={duration ? { animation: `marquee-slide ${duration}s linear infinite` } : { visibility: "hidden" }}
        >
          {data.map((item, index) => (
            <span
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              className="inline-block whitespace-nowrap text-md font-semibold"
              style={{ color: "var(--banner-text-color)", marginRight: gap }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

export default Marquee;
